/**
 * Базовая формула. Допущения:
 *  COMPONENTS:
 *    - ткань считается по площади
 *    - лента — по периметру (2*W + 2*H)
 *    - механизм — фиксированная цена за изделие
 *    - карниз — по ширине
 *  PER_SQ_M:
 *    - per_item = basePrice × площадь (компоненты игнорируются)
 *  PER_ITEM:
 *    - per_item = basePrice (компоненты игнорируются)
 *  subtotal во всех режимах = per_item × quantity.
 */
export function calcItem(input) {
    const widthM = input.widthMm / 1000;
    const heightM = input.heightMm / 1000;
    const areaSqM = widthM * heightM;
    const perimeterM = 2 * (widthM + heightM);
    // BigInt не умеет в плавающую точку, поэтому считаем в тысячных
    const areaThousandths = BigInt(Math.round(areaSqM * 1000));
    const perimeterThousandths = BigInt(Math.round(perimeterM * 1000));
    const widthThousandths = BigInt(Math.round(widthM * 1000));
    const mode = input.pricingMode ?? 'COMPONENTS';
    let fabricCost = 0n;
    let ribbonCost = 0n;
    let mechanismCost = 0n;
    let corniceCost = 0n;
    let baseCost = 0n;
    let perItem = 0n;
    if (mode === 'COMPONENTS') {
        fabricCost = (input.fabricPricePerSqMTiyin * areaThousandths) / 1000n;
        ribbonCost = (input.ribbonPricePerMTiyin * perimeterThousandths) / 1000n;
        corniceCost = (input.cornicePricePerMTiyin * widthThousandths) / 1000n;
        mechanismCost = input.mechanismPriceTiyin;
        perItem = fabricCost + ribbonCost + mechanismCost + corniceCost;
    }
    else if (mode === 'PER_SQ_M') {
        // basePrice — за квадратный метр, умножаем на площадь
        baseCost = ((input.basePriceTiyin ?? 0n) * areaThousandths) / 1000n;
        perItem = baseCost;
    }
    else {
        // PER_ITEM: фиксированная цена за изделие
        baseCost = input.basePriceTiyin ?? 0n;
        perItem = baseCost;
    }
    const subtotal = perItem * BigInt(input.quantity);
    return {
        areaSqM,
        perimeterM,
        fabricCostTiyin: fabricCost,
        ribbonCostTiyin: ribbonCost,
        mechanismCostTiyin: mechanismCost,
        corniceCostTiyin: corniceCost,
        baseCostTiyin: baseCost,
        perItemTiyin: perItem,
        subtotalTiyin: subtotal,
    };
}
/**
 * Применение наценки и скидки к итогу заказа.
 * markupPct и discountPct в процентах (0..100).
 */
export function applyMarkupAndDiscount(subtotalTiyin, markupPct, discountPct) {
    // считаем через basis points (10000 = 100%) для точности
    const afterMarkup = (subtotalTiyin * BigInt(10000 + Math.round(markupPct * 100))) / 10000n;
    const afterDiscount = (afterMarkup * BigInt(10000 - Math.round(discountPct * 100))) / 10000n;
    return afterDiscount;
}
/** Изделия заказа к оплате (после наценки и скидки) — до 100 сум по математике:
 *  меньше 50 сум вниз, от 50 вверх. 100 149 → 100 100, 100 151 → 100 200. */
export function roundOrderTotal(tiyin) {
    if (tiyin <= 0n)
        return tiyin;
    return ((tiyin + 5000n) / 10000n) * 10000n;
}
/** Округление тийинов до сум (отбрасываем копейки) */
export function roundToSum(tiyin) {
    return (tiyin / 100n) * 100n;
}
/** PER_RUN_M_AREA: сколько м² изделия даёт 1 погонный метр. Текстильные по прайсу
 *  заказчика: пог.м = ширина × высота ÷ 0,11. */
export const PB_AREA_PER_RUN_M_DEFAULT = 0.11;
/** Подбор ступени под размеры: from < x ≤ to (по ширине, если у правила width*, иначе по площади).
 *  Единственная ступень с границами проверяется так же: вне диапазона → null. */
export function pickPriceRule(rules, areaSqM, widthM, basisOverride) {
    for (const rule of rules) {
        const x = basisOverride
            ? (basisOverride === 'WIDTH' ? widthM : areaSqM)
            : (rule.widthFrom !== undefined || rule.widthTo !== undefined ? widthM : areaSqM);
        const from = rule.widthFrom ?? rule.areaFrom;
        const to = rule.widthTo ?? rule.areaTo;
        if ((from === undefined || x > from) && (to === undefined || x <= to))
            return rule;
    }
    return null;
}
/** Расчётный множитель ступени: эффективная площадь (кв.м) / метраж (м) / 1 — с минималками. */
export function priceRuleFactor(rule, widthMm, heightMm) {
    const widthM = widthMm / 1000;
    const areaSqM = widthM * (heightMm / 1000);
    if (rule.unit === 'PER_SQM') {
        let eff = areaSqM;
        let minApplied = false;
        if (rule.ceilAreaToInt) {
            const ceiled = Math.max(1, Math.ceil(eff - 1e-9));
            minApplied = ceiled !== eff;
            eff = ceiled;
        }
        else if (rule.minAreaSqM && eff < rule.minAreaSqM) {
            eff = rule.minAreaSqM;
            minApplied = true;
        }
        return { qtyFactor: eff, minApplied };
    }
    if (rule.unit === 'PER_RUN_M') {
        if (rule.minRunM && widthM < rule.minRunM)
            return { qtyFactor: rule.minRunM, minApplied: true };
        return { qtyFactor: widthM, minApplied: false };
    }
    if (rule.unit === 'PER_RUN_M_AREA') {
        const perRunM = rule.areaPerRunM && rule.areaPerRunM > 0 ? rule.areaPerRunM : PB_AREA_PER_RUN_M_DEFAULT;
        const runM = areaSqM / perRunM;
        if (rule.minRunM && runM < rule.minRunM)
            return { qtyFactor: rule.minRunM, minApplied: true };
        return { qtyFactor: runM, minApplied: false };
    }
    return { qtyFactor: 1, minApplied: false };
}
/** Цена одного изделия в USD-центах: тариф × множитель (множитель — целочисленно в тысячных). */
export function priceRuleItemCents(unitCents, qtyFactor) {
    return Math.round((unitCents * Math.round(qtyFactor * 1000)) / 1000);
}
/**
 * Несколько тканей в одном изделии (Текстильные): размер общий, расчётный метраж
 * делится между тканями поровну, каждая доля — по цене своей ткани. Одна ткань —
 * ровно priceRuleItemCents. Возвращает центы каждой доли в порядке тканей.
 * `weights` — доли не поровну (раскладка ламелей: сколько ламелей у каждой ткани).
 */
export function pbSplitItemCents(unitCents, qtyFactor, weights) {
    const factors = pbFabricQtyFactors(qtyFactor, unitCents.length, weights);
    return unitCents.map((cents, index) => priceRuleItemCents(cents, factors[index]));
}
/** Расчётный множитель каждой ткани: по весам, если они подходят, иначе поровну. */
export function pbFabricQtyFactors(qtyFactor, count, weights) {
    const sum = weights?.length === count ? weights.reduce((acc, w) => acc + Math.max(0, w), 0) : 0;
    return Array.from({ length: count }, (_, index) => (sum > 0 ? (qtyFactor * Math.max(0, weights[index])) / sum : qtyFactor / count));
}
/** «Через одну» — по сколько ламелей подряд. */
export const PB_LAMELLA_STEPS = [1, 2, 3];
/** Ламелей на ширину окна: ширина ÷ 0,11, вверх до целой (3,71 м → 34). */
export function pbLamellaTotal(widthMm, pitchM = PB_AREA_PER_RUN_M_DEFAULT) {
    if (!Number.isFinite(widthMm) || widthMm <= 0 || !(pitchM > 0))
        return 0;
    return Math.ceil(widthMm / 1000 / pitchM - 1e-9);
}
/** Больше этого по краям нельзя: в середине остаётся хотя бы одна ламель. */
export const pbLamellaMaxEdge = (total) => Math.max(1, Math.floor((total - 1) / 2));
/** Раскладывает ламели по выбранной картинке. */
export function pbLamellaPlan(layout, total) {
    const first = layout.swapped ? 1 : 0;
    const second = layout.swapped ? 0 : 1;
    const edge = Math.min(Math.max(1, Math.floor(layout.edge)), pbLamellaMaxEdge(total));
    const step = Math.max(1, Math.floor(layout.step));
    const plan = Array.from({ length: Math.max(0, total) }, (_, i) => {
        if (layout.kind === 'EDGES')
            return i < edge || i >= total - edge ? first : second;
        return Math.floor(i / step) % 2 === 0 ? first : second;
    });
    const ones = plan.filter((f) => f === 1).length;
    return { plan, counts: [plan.length - ones, ones], edge, total: plan.length };
}
/** Раскладка из DTO / JSON снимка; мусор — null (как будто раскладки нет). */
export function normalizeLamellaLayout(value) {
    if (!value || typeof value !== 'object')
        return null;
    const raw = value;
    if (raw.kind !== 'EDGES' && raw.kind !== 'ALTERNATE')
        return null;
    const edge = Number(raw.edge);
    const step = Number(raw.step);
    return {
        kind: raw.kind,
        edge: Number.isInteger(edge) && edge >= 1 ? edge : 1,
        step: PB_LAMELLA_STEPS.includes(step) ? step : 1,
        swapped: raw.swapped === true,
    };
}
const RU_LAMELLA_WORDS = {
    edges: (fabric, n) => `края — ${fabric}, по ${n} шт`,
    middle: (fabric, n) => `середина — ${fabric}, ${n} шт`,
    alternate: (step, parts) => `через одну по ${step}: ${parts}`,
    part: (fabric, n) => `${fabric} — ${n} шт`,
};
/**
 * Раскладка из снимка изделия одной строкой (без слова «Раскладка»):
 * «края — SMF · 12, по 8 шт; середина — SMF · 09, 18 шт». `fabrics` — подписи
 * основной и второй ткани. null — раскладки нет или тканей не две.
 */
export function pbLamellaLayoutText(snapshot, fabrics, words = RU_LAMELLA_WORDS) {
    const layout = normalizeLamellaLayout(snapshot);
    const raw = snapshot;
    const counts = Array.isArray(raw?.counts) ? raw.counts.map(Number) : [];
    if (!layout || fabrics.length !== 2 || counts.length !== 2 || counts.some((n) => !Number.isInteger(n)))
        return null;
    const first = layout.swapped ? 1 : 0;
    const second = layout.swapped ? 0 : 1;
    if (layout.kind === 'EDGES') {
        return `${words.edges(fabrics[first], layout.edge)}; ${words.middle(fabrics[second], counts[second])}`;
    }
    const parts = [first, second].map((index) => words.part(fabrics[index], counts[index])).join(', ');
    return words.alternate(layout.step, parts);
}
/** Раскладка коротко, без названий тканей: «8 / 18 / 8» или «через одну по 1». */
export function pbLamellaLayoutShort(snapshot) {
    const layout = normalizeLamellaLayout(snapshot);
    const raw = snapshot;
    const total = Number(raw?.total);
    if (!layout || !Number.isInteger(total) || total < 3)
        return null;
    const { edge } = pbLamellaPlan(layout, total);
    return layout.kind === 'EDGES' ? `${edge} / ${total - edge * 2} / ${edge}` : `через одну по ${layout.step}`;
}
/** Снимок для изделия: null, если ширины нет. */
export function pbLamellaSnapshot(layout, widthMm) {
    const total = pbLamellaTotal(widthMm);
    if (total < 3)
        return null;
    const { counts, edge } = pbLamellaPlan(layout, total);
    return { ...layout, edge, total, counts };
}
const positiveLimit = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
/** Что в размере выходит за ограничения варианта. На цену не влияет — только предупреждение. */
export function pbSizeLimitIssues(limits, widthMm, heightMm) {
    if (!limits || !(widthMm > 0) || !(heightMm > 0))
        return [];
    const widthM = widthMm / 1000;
    const heightM = heightMm / 1000;
    const areaSqM = widthM * heightM;
    const eps = 1e-9;
    const issues = [];
    const maxWidthM = positiveLimit(limits.maxWidthM);
    const maxHeightM = positiveLimit(limits.maxHeightM);
    const maxAreaSqM = positiveLimit(limits.maxAreaSqM);
    if (maxWidthM != null && widthM > maxWidthM + eps)
        issues.push({ kind: 'WIDTH', value: widthM, max: maxWidthM });
    if (maxHeightM != null && heightM > maxHeightM + eps)
        issues.push({ kind: 'HEIGHT', value: heightM, max: maxHeightM });
    if (maxAreaSqM != null && areaSqM > maxAreaSqM + eps)
        issues.push({ kind: 'AREA', value: areaSqM, max: maxAreaSqM });
    return issues;
}
/** Есть ли у варианта хоть одно ограничение размера. */
export const pbHasSizeLimits = (limits) => !!limits && [limits.maxWidthM, limits.maxHeightM, limits.maxAreaSqM].some((v) => positiveLimit(v) != null);
/**
 * Центы USD → тийины по курсу (сум за 1 USD).
 * тийин = центы × курс: $1 = 100 центов, 1 сум = 100 тийин.
 * Курс в 4 знаках (Decimal 18,4) → целочисленно через rate×10⁴.
 */
export function usdCentsToTiyin(usdCents, rate) {
    const rateE4 = BigInt(Math.round(rate * 10_000));
    return (BigInt(usdCents) * rateE4 + 5000n) / 10000n;
}
export class MechanismSurchargeError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
    }
}
/**
 * Точная доплата сильнее тарифа вида, тариф вида сильнее общего. Названия не
 * участвуют: товар сопоставляется по stable logicalId, вид — по sectionCode.
 */
export function selectMechanismSurcharge(rows, input) {
    const ranked = rows.flatMap((row) => {
        if (row.mechanismTypeId !== input.mechanismTypeId)
            return [];
        const sectionSpecific = row.sectionCodes.length > 0;
        if (sectionSpecific && !row.sectionCodes.includes(input.sectionCode))
            return [];
        const targetSpecific = row.targetRowLogicalIds.length > 0;
        if (targetSpecific && !row.targetRowLogicalIds.includes(input.materialRowLogicalId))
            return [];
        return [{ row, rank: (targetSpecific ? 4 : 0) + (sectionSpecific ? 2 : 0) + 1 }];
    }).sort((left, right) => right.rank - left.rank);
    const best = ranked[0];
    if (!best)
        return null;
    if (ranked[1]?.rank === best.rank) {
        throw new MechanismSurchargeError('mechanism_surcharge_ambiguous');
    }
    if (best.row.priceCents == null) {
        throw new MechanismSurchargeError('mechanism_surcharge_price_empty');
    }
    return best.row;
}
export function calculateMechanismSurchargeCents(row, input) {
    if (!row)
        return 0;
    const widthM = input.widthMm / 1_000;
    const areaSqM = widthM * (input.heightMm / 1_000);
    const factor = row.unit === input.baseUnit
        ? input.baseQtyFactor
        : row.unit === 'PER_SQM'
            ? areaSqM
            : row.unit === 'PER_RUN_M'
                ? widthM
                : 1;
    const itemCents = Math.round((row.priceCents ?? 0) * Math.round(factor * 1_000) / 1_000);
    return itemCents * input.quantity;
}
